/**
 * Demo tenants manifest (frontend-side).
 *
 * Mirrors `shared/src/demo/tenants.ts` in the backend monorepo. Kept
 * duplicated here because `@leviathan/shared` is a Lambda-layer package
 * (Node-only) — the frontend bundle cannot import it directly.
 *
 * Keep this file in sync with the backend when adding/removing demos.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Briefcase,
  Cloud,
  ShoppingCart,
  Shirt,
  UtensilsCrossed,
} from 'lucide-react'

export type DemoAccentColor = 'blue' | 'purple' | 'green' | 'orange' | 'pink'

export interface DemoTenantConfig {
  slug: string
  templateId: string
  displayName: string
  description: string
  iconName: string
  icon: LucideIcon
  accentColor: DemoAccentColor
  email: string
  startYear: number
  endYear: number
  /**
   * When true, the demo is provisioned but hidden from the picker UI because
   * the generator is still MVP-level (dashboards would 500 or show empty).
   */
  comingSoon?: boolean
}

const ICONS: Record<string, LucideIcon> = {
  ShoppingCart,
  Briefcase,
  Cloud,
  UtensilsCrossed,
  Shirt,
}

export const DEMO_TENANTS: readonly DemoTenantConfig[] = [
  {
    slug: 'ecommerce',
    templateId: 'ecommerce-full',
    displayName: 'E-commerce Retail',
    description:
      'Retailer B2C + B2B: ordini multicanale, catalogo prodotti, RFM, inventario, margini e KPI economici.',
    iconName: 'ShoppingCart',
    icon: ICONS.ShoppingCart,
    accentColor: 'blue',
    email: 'demo-ecommerce@leviathanbi.com',
    startYear: 2020,
    endYear: 2028,
  },
  {
    slug: 'salesagent',
    templateId: 'sales-agent',
    displayName: 'B2B Sales Agent',
    description:
      'Distributore B2B con agenti di vendita: territorio, mappa clienti con geolocalizzazione, brand/prodotti, fatturato annuale e YoY.',
    iconName: 'Briefcase',
    icon: ICONS.Briefcase,
    accentColor: 'purple',
    email: 'demo-sales-agent@leviathanbi.com',
    startYear: 2020,
    endYear: 2028,
  },
  {
    slug: 'saas',
    templateId: 'saas',
    displayName: 'SaaS Subscription',
    description:
      'Business subscription: MRR/ARR, churn, CAC/LTV, coorti, piani tariffari. Scenario startup tech con curva di maturazione.',
    iconName: 'Cloud',
    icon: ICONS.Cloud,
    accentColor: 'green',
    email: 'demo-saas@leviathanbi.com',
    startYear: 2020,
    endYear: 2028,
  },
  {
    slug: 'restaurant',
    templateId: 'restaurant',
    displayName: 'Restaurant Group',
    description:
      'Gruppo ristorazione multi-location: coperti, scontrino medio, menu performance, table turns. Include crash COVID 2020.',
    iconName: 'UtensilsCrossed',
    icon: ICONS.UtensilsCrossed,
    accentColor: 'orange',
    email: 'demo-restaurant@leviathanbi.com',
    startYear: 2020,
    endYear: 2028,
  },
  {
    slug: 'fashion',
    templateId: 'fashion-retail',
    displayName: 'Fashion Retail',
    description:
      'Retail moda omnicanale: collezioni SS/FW, varianti taglia/colore, sell-through, markdown, resi, negozi fisici + e-commerce.',
    iconName: 'Shirt',
    icon: ICONS.Shirt,
    accentColor: 'pink',
    email: 'demo-fashion@leviathanbi.com',
    startYear: 2020,
    endYear: 2028,
  },
]

/** Demos available in the picker (excludes comingSoon entries). */
export const VISIBLE_DEMO_TENANTS: readonly DemoTenantConfig[] = DEMO_TENANTS.filter(
  (d) => d.comingSoon !== true,
)

export function getDemoBySlug(slug: string): DemoTenantConfig | null {
  return DEMO_TENANTS.find((t) => t.slug === slug) ?? null
}

export function listDemoSlugs(): readonly string[] {
  return DEMO_TENANTS.map((t) => t.slug)
}

/** Tailwind class fragments keyed by accent colour. Safe-listed by full strings. */
export const ACCENT_CLASSES: Record<
  DemoAccentColor,
  { ring: string; iconBg: string; iconFg: string; btn: string; hover: string }
> = {
  blue: {
    ring: 'group-hover:ring-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/40',
    iconFg: 'text-blue-600 dark:text-blue-300',
    btn: 'bg-blue-600 hover:bg-blue-700 text-white',
    hover: 'hover:border-blue-400',
  },
  purple: {
    ring: 'group-hover:ring-purple-400',
    iconBg: 'bg-purple-100 dark:bg-purple-900/40',
    iconFg: 'text-purple-600 dark:text-purple-300',
    btn: 'bg-purple-600 hover:bg-purple-700 text-white',
    hover: 'hover:border-purple-400',
  },
  green: {
    ring: 'group-hover:ring-green-400',
    iconBg: 'bg-green-100 dark:bg-green-900/40',
    iconFg: 'text-green-600 dark:text-green-300',
    btn: 'bg-green-600 hover:bg-green-700 text-white',
    hover: 'hover:border-green-400',
  },
  orange: {
    ring: 'group-hover:ring-orange-400',
    iconBg: 'bg-orange-100 dark:bg-orange-900/40',
    iconFg: 'text-orange-600 dark:text-orange-300',
    btn: 'bg-orange-600 hover:bg-orange-700 text-white',
    hover: 'hover:border-orange-400',
  },
  pink: {
    ring: 'group-hover:ring-pink-400',
    iconBg: 'bg-pink-100 dark:bg-pink-900/40',
    iconFg: 'text-pink-600 dark:text-pink-300',
    btn: 'bg-pink-600 hover:bg-pink-700 text-white',
    hover: 'hover:border-pink-400',
  },
}
